import { createLogger } from '@ctrl-enter/tools/utils';
import { Subscription, User } from '@ctrl-enter/types';

const logger = createLogger('web:store:session');
import { Sentry } from '../services/sentry';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

type SessionState = {
  user: User | null;
  isSubscribed: boolean;
  hasSignedAgreement: boolean;
  setUser: (user: User | null) => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  setHasSignedAgreement: (hasSignedAgreement: boolean) => void;
  clearSession: () => void;
};

export const useSessionStore = create<SessionState>()(
  devtools(
    persist(
      set => ({
        user: null,
        isSubscribed: false,
        hasSignedAgreement: false,
        setUser: user => {
          // Update Sentry user context for replays
          if (user) {
            logger.info('🔐 [SENTRY] Setting user context:', {
              id: user.id,
              email: user.email,
              organizationId: user.organizationId,
              role: user.role,
              environment: process.env.NODE_ENV || 'development',
            });

            Sentry.setUser({
              id: user.id,
              email: user.email,
              username: user.name,
            });

            // Add additional tags for better filtering in Sentry UI
            Sentry.setTag('organization_id', user.organizationId);
            Sentry.setTag('user_role', user.role);
            Sentry.setTag('environment', process.env.NODE_ENV || 'development');
          } else {
            logger.info('🔐 [SENTRY] Clearing user context');
            Sentry.setUser(null);
            Sentry.setTag('organization_id', null);
            Sentry.setTag('user_role', null);
            Sentry.setTag('environment', null);
          }

          set({ user });
        },
        setIsSubscribed: isSubscribed => set({ isSubscribed }),
        setHasSignedAgreement: hasSignedAgreement => set({ hasSignedAgreement }),
        clearSession: () => {
          logger.info('🔐 [SENTRY] Clearing session and user context');

          // Clear Sentry user context on logout
          Sentry.setUser(null);

          set({ user: null, isSubscribed: false, hasSignedAgreement: false });
        },
      }),
      {
        name: 'session-store',
        partialize: state => ({
          user: state.user,
          isSubscribed: state.isSubscribed,
          hasSignedAgreement: state.hasSignedAgreement,
        }),
      }
    ),
    { name: 'session-store' }
  )
);

// Selector hooks
export const useUser = () => useSessionStore(state => state.user);

export const isSubscribed = (subscription?: Subscription | null) => {
  const validStatuses = ['active', 'trialing'];
  const isValidStatus = subscription ? validStatuses.includes(subscription.status) : false;
  const isNotFree = subscription?.planId !== 'free';
  const result = subscription ? isValidStatus && isNotFree : false;

  logger.info(
    '🔍 [SESSION-STORE] isSubscribed check:',
    JSON.stringify(
      {
        hasSubscription: !!subscription,
        subscription: subscription,
        status: subscription?.status,
        planId: subscription?.planId,
        isValidStatus,
        isNotFree,
        validStatuses,
        result,
      },
      null,
      2
    )
  );

  if (!subscription) return false;
  return isValidStatus && isNotFree;
};
